/**
 * The two decisions the composer makes, with no DOM in either. F-148.
 *
 * **Its own module for `keyboard.ts`'s reason**: a decision that cannot be tested is one that
 * gets argued about instead. `Composer.tsx` keeps the parts that genuinely need a browser —
 * reading a computed style, measuring `scrollHeight`, calling `preventDefault` — and holds no
 * policy of its own, so `packages/web/test/chat-composer.test.ts` can assert both decisions
 * against plain numbers.
 *
 * Note the word "keyboard" means two different things on this screen and they share no code.
 * `keyboard.ts` is the *on-screen* keyboard covering the composer, which is a viewport
 * question; this file is about a key that was pressed. They are deliberately not merged: the
 * first has five shipped fixes and a suite of device readings behind it, and nothing here
 * should ever be a reason to open that file.
 */

/** What a key press in the composer should do. */
export type ComposerKey =
  /** Prevent the default and send what is in the box. */
  | 'send'
  /** Hand it back to the browser untouched — a newline, or an IME committing a character. */
  | 'pass'
  /** Prevent the default and do nothing. Enter on a box with nothing in it. */
  | 'block';

/** A key press, in the terms a `KeyboardEvent` reports one. */
export interface KeyPress {
  key: string;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  /**
   * Whether an input method editor is mid-composition.
   *
   * **The one case where intercepting Enter destroys something rather than sending it.** A
   * Japanese, Chinese or Korean keyboard — and the accent composer on any platform — uses
   * Enter to commit the candidate it is showing. Sending on that key would post the raw
   * romaji and throw away the character the learner was choosing, and they would have no way
   * of knowing why. Nothing on this screen is worth that, so composition always wins.
   */
  isComposing: boolean;
  /**
   * The legacy signal for the same thing, and it is not redundant.
   *
   * Safari and several Android keyboards report `keyCode` 229 for every key consumed by an
   * IME while leaving `isComposing` false on the `keydown` that commits. Checking only the
   * modern flag therefore still eats the first committed character on exactly the platforms
   * this app is used on. It is optional because a synthetic event need not carry it.
   */
  keyCode?: number;
}

/**
 * What Enter does, which is now what it does everywhere else.
 *
 * Leo, 2026-09-17: *"enter should send, we're not writing long paragraphs in that field."*
 * Before this, Enter inserted a newline and only Cmd/Ctrl+Enter sent — the binding a code
 * editor uses, in a box whose whole job is answering a question in a sentence. Shift+Enter
 * keeps the newline, because it is the convention every other message box teaches and because
 * an explanation occasionally wants a second line.
 *
 * `canSend` is the same predicate the send button is disabled by, passed in rather than
 * recomputed, so the key and the button can never disagree about whether there is anything to
 * send. When it is false Enter is *blocked* rather than passed: a newline typed into an empty
 * box grows the field, leaves the button disabled, and looks like the app having ignored a
 * keypress that in fact did something invisible.
 */
export function composerKey(press: KeyPress, canSend: boolean): ComposerKey {
  if (press.key !== 'Enter') return 'pass';
  // Composition first, before any other test: see `isComposing`.
  if (press.isComposing || press.keyCode === 229) return 'pass';
  /*
   * Shift and Alt are the newline. Ctrl and Meta are deliberately *not* listed here, so
   * Cmd/Ctrl+Enter falls through and still sends — anybody who learned the old binding in the
   * days it was live keeps it, and nobody has to discover that their habit now does nothing.
   */
  if (press.shiftKey || press.altKey) return 'pass';
  return canSend ? 'send' : 'block';
}

/**
 * The tallest the box grows before it starts scrolling inside itself.
 *
 * Three rows and not four, and the floor matters more than the ceiling here. Two would be
 * wrong whatever the field is for: the placeholder wraps to two lines on a phone (see
 * `placeholderHeight`), so a two-row ceiling is a box that scrolls while it is still empty.
 * Three is one line of room past the longest thing the empty box ever shows, and it is what
 * "not writing long paragraphs in that field" costs a learner who writes one anyway: a short
 * scroll rather than a field that has eaten the question they are answering.
 */
export const MAX_ROWS = 3;

/** What the box measures, all of it read from the element itself. */
export interface BoxMetrics {
  /** The computed `line-height`, in pixels. One row. */
  lineHeight: number;
  /** `padding-top` + `padding-bottom`, in pixels. */
  paddingY: number;
  /** `border-top-width` + `border-bottom-width`, in pixels. */
  borderY: number;
  /**
   * `scrollHeight` with the height reset to `auto` — the content plus the padding.
   *
   * **It does not include the border**, and that omission is the bug this function exists to
   * stop coming back. `box-sizing: border-box` is global in `styles.css`, so a height set
   * from a bare `scrollHeight` is two pixels short of the content it was measured from, and
   * the box is scrollable by those two pixels from the very first character. That is a
   * one-line field with a scrollbar in it on a laptop and a field that rubber-bands under a
   * thumb on a phone — reported as the text box's scroll looking weird, which is exactly what
   * it is.
   */
  scrollHeight: number;
  /**
   * The same measurement taken with the placeholder as the value, or `undefined` when the box
   * is not empty.
   *
   * A placeholder contributes nothing to `scrollHeight`, so an empty box measures one line
   * however many lines the placeholder occupies — and "Answer the question, or ask for
   * something." takes two on a phone. The second line then overflowed a box padded for one,
   * which reads as a field whose bottom margin is smaller than its top, and was reported that
   * way. Keeping it in the arithmetic here is what stops a resize quietly reintroducing it.
   */
  placeholderHeight?: number;
}

/**
 * Sub-pixel jitter is not a new width.
 *
 * `ResizeObserver` reports fractional widths, and a flexible row beside a button whose label
 * swaps between `↑` and `…` settles on figures that differ in the third decimal place. Without
 * a threshold every one of those re-runs the measurement, which is a layout read and a write
 * per frame for a box that has not changed shape. Half a pixel is far below anything that can
 * rewrap a line and far above what rounding produces.
 */
export const WIDTH_JITTER_PX = 0.5;

/**
 * Whether an observed width is a reason to measure the box again.
 *
 * **Width, and deliberately not height**, because this observer watches an element the
 * component also resizes: every measurement ends in a write to `style.height`, which an
 * observer keyed on height would see as a change and answer with another measurement, for ever.
 *
 * **Keying on width is not by itself what makes it terminate, and it is worth being exact
 * about this rather than comfortable.** A measurement can change the width: its last act is
 * `overflowY = 'auto'` when the content overflows, and on any platform with classic scrollbars
 * that paints a scrollbar and takes about fifteen pixels out of the content box — a real width
 * change, and the observer really does fire. Three things settle it, and the first is the one
 * that looks like tidiness and is not:
 *
 * 1. **The measurement forces `overflow-y: hidden` before it reads anything** (see
 *    `Composer.tsx`), so its result is a function of the text and the scrollbar-free width and
 *    of nothing else. The re-run therefore computes an identical height and `scrolls`, and the
 *    second callback finds nothing to change. Removing that line to tidy it up does not cost a
 *    few pixels of accuracy — it costs termination.
 * 2. **The caller banks the new width before it acts on it** — `width = next` precedes
 *    `if (again) measure()` — so the post-write width is already the remembered one when the
 *    redundant measurement runs, and cannot be seen as a change a third time.
 * 3. `ResizeObserver` reports at frame boundaries, so the momentary full width *inside*
 *    `measure()`, while the overflow is forced off, is never delivered at all.
 *
 * At most two callbacks, then, and the second does no work.
 *
 * `undefined` is the first observation, which arrives as soon as anything is observed and
 * describes a box the effect on `draft` has just measured. Measuring it again would be work
 * for no reason.
 */
export function widthChanged(previous: number | undefined, next: number): boolean {
  if (previous === undefined) return false;
  return Math.abs(next - previous) > WIDTH_JITTER_PX;
}

/** How tall the box should be, and whether it has to scroll at that height. */
export interface BoxSize {
  height: number;
  scrolls: boolean;
}

/**
 * How tall the box should be for what is in it.
 *
 * `scrolls` is half the answer and not a detail. The stylesheet leaves the box
 * `overflow-y: hidden` and this turns it on only once the content genuinely exceeds the
 * ceiling, because a box that is `overflow: auto` at all times is one the browser is entitled
 * to paint a scrollbar in, and Safari rubber-bands a touch inside it whether or not there is
 * anywhere to go.
 */
export function composerBox(metrics: BoxMetrics, rows: number = MAX_ROWS): BoxSize {
  const { lineHeight, paddingY, borderY } = metrics;
  const ceiling = lineHeight * rows + paddingY + borderY;
  const content = Math.max(metrics.scrollHeight, metrics.placeholderHeight ?? 0);
  const needed = content + borderY;
  return { height: Math.min(needed, ceiling), scrolls: needed > ceiling };
}
